/**
 * StepNavSection — per-step Back/Next pair for the CartSidebar pinned bottom bar.
 *
 * Phase C-B (configurator audit follow-up): the previous design rendered Back/
 * Next inside a sticky page footer (`StepShell.footer`). That sticky footer is
 * removed entirely; nav lives here, stacked vertically below the cart totals.
 *
 * This component is now mostly presentational. All routing, gating, label and
 * analytics logic lives in `useStepNav` (catalogue / configuration /
 * instructions / billing) and the resolver `useResolvedStepFlow`. The primary
 * label is TRUTHFUL — it follows the actual `nextStep` instead of a hardcoded
 * guess that lied when the flow skipped configuration/instructions.
 *
 * Store-only since ISSUE-3392: the Summary pair used to read
 *   `usePurchaseFlowPlaceOrder` / `usePurchaseFlowOrderMeta`, legacy contexts the
 *   retired `legacyBridge/` mounted. Every value it took from them was inert on a
 *   v2 route (they track legacy mutations v2 never fires), and each has a native
 *   counterpart in the store. `SummaryNavButtons` stays a separate component
 *   because it owns the summary-only validity read, and therefore only mounts for
 *   `step === 'summary'` and never runs on the routes where the
 *   providers are absent. `useStepNav` deliberately does NOT call those hooks.
 */

import React, { useMemo } from 'react'
import { Box, Stack } from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useTranslation } from 'react-i18next'

import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { useInstructionOptions } from 'dataQueries/purchaseFlowRevampInstructions.query'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { PURCHASE_FLOW_NEXT_CTA_ATTRIBUTE } from 'utils/analytics/screenEntryLayout'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'

import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { usePfStore } from '../../store'
import { selectHasUnresolvedCartItems, selectSummaryStepErrors, selectSummaryStepValid } from '../../store/selectors'
import { PropertySelectionDrawer } from '../../entry/PropertySelectionDrawer.component'
import { useBillingPaymentMethods } from '../../steps/billing/useBillingPaymentMethods'
import { useDefaultInvoicingAddress } from '../../steps/billing/useDefaultInvoicingAddress'
import { useIsChargedAtCheckout } from '../../steps/billing/useEffectivePaymentMethod'
import { useStepNav, type StepNavStep } from './useStepNav'
import { usePfEmbedConsentState } from '../../routing/usePfEmbedConsent'

export type { StepNavStep } from './useStepNav'

/**
 * Render layout:
 *   - `'sidebar'` (default) — desktop right-rail: full-width stacked buttons,
 *     step-specific truthful label, disabled reason as a hover tooltip.
 *   - `'bar'` — mobile fixed footer: a single row of [icon-only Back] + [compact
 *     "To {destination}" primary]. Disabled reason renders as inline helper text
 *     (touch can't surface a tooltip). The progress stepper supplies wayfinding,
 *     so the primary stays short.
 */
export type StepNavLayout = 'sidebar' | 'bar'

/**
 * On the primary's wrapper, which the billing and summary entries measure for `cta_in_viewport`. The wrapper also
 * catches the clicks on a disabled primary (`pointer-events: none`), for `validation_passed: false`.
 */
const NEXT_CTA_MARKER = { [PURCHASE_FLOW_NEXT_CTA_ATTRIBUTE]: '' }

interface Props {
  /** Discriminator: which PF revamp step is rendering. Drives labels,
   *  navigation targets, and gating (via `useStepNav`). */
  step: StepNavStep
  /** See `StepNavLayout`. Defaults to `'sidebar'` so the desktop right-rail and
   *  existing call sites / tests are unaffected. */
  layout?: StepNavLayout
}

// ─── Summary-step nav (legacy context consumers) ───────────────────────────────

interface SummaryNavButtonsProps {
  onBack: () => void
  onPlaceOrder: () => void
  /** See `StepNavLayout`. `'bar'` renders [icon-only Back] + [Place order] in a
   *  row with the disabled reason inline; `'sidebar'` stacks them with a tooltip. */
  layout: StepNavLayout
  /** Accessible label for the icon-only back button in `'bar'` layout. */
  backAriaLabel: string
}

/**
 * Renders the Place Order / Go-back pair for the Summary step only.
 * Kept a separate component because it owns the summary-specific validity read
 * (`selectSummaryStepValid`) and the instruction-options / invoicing-address
 * queries that feed it — none of which the earlier steps' nav needs. Reads the
 * store only, since ISSUE-3392.
 */
const SummaryNavButtons: React.FC<SummaryNavButtonsProps> = ({ onBack, onPlaceOrder, layout, backAriaLabel }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { spawnInfoToast } = useSnackbar()
  const isBar = layout === 'bar'

  // Summary validity gate (Zustand). Catalogue powers the config/instructions sub-validators.
  const catalogue = usePfCatalogue().data
  const allowFallbackFlowResolver = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER)
  // The exact BE `visible` signal for the Instructions step. When
  // `false`, the stepper auto-redirects away from Instructions (no reachable
  // UI to set a meeting), so the gate must stop requiring one — otherwise
  // Place Order deadlocks permanently for shooting carts. See
  // `selectInstructionsStepValid` / `selectSummaryStepValid`.
  const { data: instructionOptions, isRefreshing: isInstructionOptionsRefreshing } = useInstructionOptions()
  const instructionsVisible = instructionOptions?.visible
  // PM-D2 REVERSAL (ratified, option A): same shared instruction-options React
  // Query cache entry Billing's own Next gate reads (`useStepNav.ts`) — no
  // extra request. Without this, a BE `isDefault` preselect renders checked on
  // Billing (satisfying ITS gate) but Summary's Place Order stayed disabled,
  // stranding the user with a checked radio and no further action.
  const { defaultPaymentMethod } = useBillingPaymentMethods()
  // Billing's Next gate and this one must agree about the default,
  // or Proceed opens and Summary refuses (the PM-D2 lesson).
  const defaultInvoicingAddress = useDefaultInvoicingAddress()
  const isSummaryStepValid = usePfStore(
    useMemo(
      () => selectSummaryStepValid(catalogue, allowFallbackFlowResolver, instructionsVisible, defaultPaymentMethod, defaultInvoicingAddress),
      [catalogue, allowFallbackFlowResolver, instructionsVisible, defaultPaymentMethod, defaultInvoicingAddress]
    )
  )

  // Disable Place Order while the POST is in flight so a second tap can't
  // fire a duplicate order.
  const isPlacingOrder = usePfStore((s) => s.isPlacingOrder)
  const isPaymentRetry = usePfStore((s) => s.paymentFailure !== null)
  // A Stripe cart being re-priced must not be confirmable: the
  // client would pay the amount from before the edit.
  const isIntentSyncing = usePfStore((s) => s.isIntentSyncing)
  // A card / SEPA cart also needs card details that Stripe considers complete:
  // confirming an incomplete element fails AFTER the order has been placed.
  const isStripeFormComplete = usePfStore((s) => s.isStripeFormComplete)
  // Opening or re-pricing the intent failed, so there is no payment form to fill
  // and no secret to confirm with. Published by `RevampPaymentIntentProvider`.
  const isIntentFailed = usePfStore((s) => s.isIntentFailed)
  const isStripeCart = useIsChargedAtCheckout()
  const awaitingCardDetails = isStripeCart && !isStripeFormComplete

  // Hide the secondary while a placement is in flight, so the
  // client cannot navigate out from under an order that is going out. Legacy
  // also hid it on `orderHasBeenPlaced`, which never became true on a v2 route —
  // both paths navigate away on success instead.
  const showBack = !isPlacingOrder && !isPaymentRetry

  // Gate the summary primary on Zustand validity AND the Stripe
  // conditions. This is the whole of what legacy's `isPlaceOrderButtonDisabled`
  // meant, in native terms: a placement in flight, a re-price in flight (legacy's
  // `isRecalculatingPrices`), an incomplete card element or missing receipt email
  // (`isStripeFormComplete`), and an intent that failed to open.
  // Inside the CRM embed a missing acceptance holds the order until the Summary
  // checkbox is ticked. Kept out of `selectSummaryStepValid` because consent is
  // not store state — it is fetched per workspace and per channel.
  const { isConsentBlockingOrder } = usePfEmbedConsentState()

  const hasUnresolvedItems = usePfStore(useMemo(() => selectHasUnresolvedCartItems(catalogue), [catalogue]))
  const areFeesUnknown = usePfStore((s) => !!s.instructionFeesUnknown)

  const primaryDisabled = !isSummaryStepValid
    || isConsentBlockingOrder
    || isPlacingOrder
    || isIntentSyncing
    || isIntentFailed
    || awaitingCardDetails
    || isInstructionOptionsRefreshing
    || areFeesUnknown

  // Why Place order is down: an earlier step's gap alone, as legacy reports the first one, else this step's own.
  // The card and receipt flags are null while the payment form is not on screen. A click while placing is a wait.
  const trackAnalytics = usePfRevampAnalytics()
  const isStripeCardComplete = usePfStore((s) => s.isStripeCardComplete)
  const isStripeReceiptEmailValid = usePfStore((s) => s.isStripeReceiptEmailValid)
  const onPlaceOrderBlocked = () => {
    if (!primaryDisabled || isPlacingOrder) return
    const earlierStepErrors = selectSummaryStepErrors(
      catalogue, allowFallbackFlowResolver, instructionsVisible, defaultPaymentMethod, defaultInvoicingAddress
    )(usePfStore.getState())
    const summaryErrors: NextStepValidationError[] = []
    if (isConsentBlockingOrder) summaryErrors.push('consent_missing')
    if (isIntentSyncing || isInstructionOptionsRefreshing || areFeesUnknown) summaryErrors.push('prices_recalculating')
    if (awaitingCardDetails && isStripeReceiptEmailValid === false) summaryErrors.push('receipt_email_invalid')
    if (awaitingCardDetails && isStripeCardComplete === false) summaryErrors.push('card_details_incomplete')
    if (isStripeCart && isIntentFailed) summaryErrors.push('payment_setup_failed')
    const validationErrors = earlierStepErrors.length > 0 ? earlierStepErrors : summaryErrors
    trackAnalytics({
      type: PfRevampEventName.STEP_ADVANCE_BLOCKED,
      payload: { fromStep: 'summary', toStep: 'summary', validationErrors: validationErrors.length > 0 ? validationErrors : ['unexplained_block'] },
    })
  }

  // Provide a tooltip when Place Order is disabled.
  // SX2-F01: split copy — Stripe-invalid shows payment-specific message;
  // general invalid (invoice carts, mount window) shows a neutral message
  // that doesn't confuse users by asking them to "complete payment details"
  // when no payment form exists.
  // F10 extension: when the paymentIntent request itself errored (server
  // 500 / network failure), show an actionable retry message instead of
  // asking the user to "complete payment details" — which is misleading
  // when the failure is server-side.
  const primaryTooltip = (() => {
    if (!primaryDisabled || isPlacingOrder) return ''
    if (hasUnresolvedItems) return t('purchase_flow_revamp:cart.proceed_requires_unavailable_removed')
    if (areFeesUnknown) return t('purchase_flow_revamp:cart.proceed_requires_fees_known')
    if (isStripeCart && isIntentFailed) return t('purchase_flow_revamp:cart.proceed_requires_payment_intent_error')
    if (awaitingCardDetails) return t('purchase_flow_revamp:cart.proceed_requires_summary_valid')
    return t('purchase_flow_revamp:cart.proceed_requires_order_complete')
  })()

  return (
    <>

      {/* Bar layout (mobile footer): [icon-only Back] + [Place order] in a row,
          disabled reason inline beneath. */}
      {isBar &&
        <Stack gap={0.8} data-cy="pf-revamp-cart-step-nav">

          <Stack direction="row" gap={0.8} alignItems="stretch">

            {showBack &&
              <MUIButton
                type="secondaryBorder"
                size="md"
                onClick={onBack}
                aria-label={backAriaLabel}
                data-cy="pf-revamp-cart-back-wrap"
                sx={{ minWidth: '4.4rem', px: 1.2, flexShrink: 0 }}
              >
                <ArrowBackRoundedIcon sx={{ fontSize: '2rem' }} />
              </MUIButton>
            }

            {/* Disabled tap surfaces the reason as a toast (touch can't hover a
                tooltip) — keeps the footer to a single uncluttered row. The
                disabled button has pointer-events:none, so the click lands on
                this wrapper. */}
            <Box
              sx={{ flex: 1 }}
              data-cy="pf-revamp-cart-proceed-wrap"
              {...NEXT_CTA_MARKER}
              onClick={primaryDisabled
                ? () => {
                  onPlaceOrderBlocked()
                  if (primaryTooltip) spawnInfoToast(primaryTooltip)
                }
                : undefined}
            >
              <MUIButton
                type="defaultPrimary"
                size="md"
                fullWidth
                disabled={primaryDisabled && !isPlacingOrder}
                isLoading={isPlacingOrder}
                onClick={onPlaceOrder}
              >
                {isPaymentRetry ? t('order:step_validation.retry_payment') : t('purchase_flow_revamp:cart.place_order')}
              </MUIButton>
            </Box>

          </Stack>

        </Stack>
      }

      {/* Sidebar layout (desktop right-rail): stacked full-width buttons + tooltip. */}
      {!isBar &&
        <>

          <Box data-cy="pf-revamp-cart-proceed-wrap" {...NEXT_CTA_MARKER} onClick={primaryDisabled ? onPlaceOrderBlocked : undefined}>

            <MUITooltip content={primaryTooltip} placement="top">
              <MUIButton
                type="defaultPrimary"
                size="md"
                fullWidth
                disabled={primaryDisabled && !isPlacingOrder}
                isLoading={isPlacingOrder}
                onClick={onPlaceOrder}
              >
                {isPaymentRetry ? t('order:step_validation.retry_payment') : t('purchase_flow_revamp:cart.place_order')}
              </MUIButton>
            </MUITooltip>

          </Box>

          {showBack &&
            <Box data-cy="pf-revamp-cart-back-wrap">

              <MUIButton
                type="secondaryNoBorder"
                size="md"
                fullWidth
                onClick={onBack}
              >
                {t('purchase_flow_revamp:cart.back_to_billing')}
              </MUIButton>

            </Box>
          }

        </>
      }

    </>
  )
}

// ─── Main component ─────────────────────────────────────────────────────────────

/**
 * @component StepNavSection
 * Per-step nav row inside the CartSidebar pinned bottom bar. Presentational
 * shell over `useStepNav`. See file header for design rationale.
 */
export const StepNavSection: React.FC<Props> = ({ step, layout = 'sidebar' }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  const {
    primaryLabel,
    primaryLabelShort,
    primaryDisabled,
    primaryReason,
    primaryBlockedMessage,
    onPrimary,
    onPrimaryBlocked,
    secondaryLabel,
    onSecondary,
    isSummary,
    isPropertyDrawerOpen,
    setPropertyDrawerOpen,
  } = useStepNav(step)

  const { spawnInfoToast } = useSnackbar()

  const isBar = layout === 'bar'

  // Bar (mobile footer) uses the compact "To {destination}" label — the stepper
  // supplies wayfinding. Sidebar (desktop) keeps the truthful specific label.
  const displayPrimaryLabel = isBar ? primaryLabelShort : primaryLabel
  // The icon-only back button needs an accessible label; reuse the descriptive
  // secondary copy ("Go back to the catalog", etc.).
  const backAriaLabel = secondaryLabel ?? t('purchase_flow_revamp:cart.back_to_billing')

  return (
    <>

      <Stack gap={isBar ? 0.8 : 1.2} data-cy="pf-revamp-cart-step-nav">

        {isSummary &&
          <SummaryNavButtons
            onBack={onSecondary}
            onPlaceOrder={onPrimary}
            layout={layout}
            backAriaLabel={backAriaLabel}
          />
        }

        {/* Bar layout (mobile footer): [icon-only Back] + [compact primary]. */}
        {!isSummary && isBar &&
          <>

            <Stack direction="row" gap={0.8} alignItems="stretch">

              {!!secondaryLabel &&
                <MUIButton
                  type="secondaryBorder"
                  size="md"
                  onClick={onSecondary}
                  aria-label={backAriaLabel}
                  data-cy="pf-revamp-cart-back-wrap"
                  sx={{ minWidth: '4.4rem', px: 1.2, flexShrink: 0 }}
                >
                  <ArrowBackRoundedIcon sx={{ fontSize: '2rem' }} />
                </MUIButton>
              }

              {/* Disabled tap surfaces the reason as a toast (touch can't hover
                  a tooltip) — keeps the footer to a single uncluttered row. The
                  disabled button has pointer-events:none, so the click lands on
                  this wrapper. */}
              <Box
                sx={{ flex: 1, minWidth: 0 }}
                data-cy="pf-revamp-cart-proceed-wrap"
                {...NEXT_CTA_MARKER}
                onClick={primaryDisabled
                  ? () => {
                    onPrimaryBlocked()
                    if (primaryBlockedMessage) spawnInfoToast(primaryBlockedMessage)
                  }
                  : undefined}
              >
                <MUIButton
                  type="defaultPrimary"
                  size="md"
                  fullWidth
                  disabled={primaryDisabled}
                  onClick={onPrimary}
                >
                  {displayPrimaryLabel}
                </MUIButton>
              </Box>

            </Stack>

          </>
        }

        {/* Sidebar layout (desktop right-rail): stacked full-width buttons + tooltip. */}
        {!isSummary && !isBar &&
          <>

            {/* A click on the disabled button says what is missing too, not only a hover. */}
            <Box
              data-cy="pf-revamp-cart-proceed-wrap"
              {...NEXT_CTA_MARKER}
              onClick={primaryDisabled
                ? () => {
                  onPrimaryBlocked()
                  if (primaryBlockedMessage) spawnInfoToast(primaryBlockedMessage)
                }
                : undefined}
            >

              <MUITooltip content={primaryReason} placement="top">
                <MUIButton
                  type="defaultPrimary"
                  size="md"
                  fullWidth
                  disabled={primaryDisabled}
                  onClick={onPrimary}
                >
                  {displayPrimaryLabel}
                </MUIButton>
              </MUITooltip>

            </Box>

            {!!secondaryLabel &&
              <Box data-cy="pf-revamp-cart-back-wrap">

                <MUIButton
                  type="secondaryNoBorder"
                  size="md"
                  fullWidth
                  onClick={onSecondary}
                >
                  {secondaryLabel}
                </MUIButton>

              </Box>
            }

          </>
        }

      </Stack>

      <PropertySelectionDrawer
        isOpen={isPropertyDrawerOpen}
        onClose={() => setPropertyDrawerOpen(false)}
      />

    </>
  )
}
